export type Role = 'none' | 'viewer' | 'commenter' | 'editor' | 'owner';
export type Access = 'none' | 'view' | 'comment' | 'edit';
export type MemberRole = 'viewer' | 'commenter' | 'editor';
export type OrgRole = 'member' | 'admin' | 'owner';

export interface User {
  id: string;
  email: string;
  name: string;
  color: string;
  locale: 'ru' | 'en';
  emailVerified: boolean;
}

export interface Team {
  id: string;
  orgId: string | null;
  name: string;
  personal: boolean;
  role: OrgRole;
  members: number;
}

export interface Org {
  id: string;
  name: string;
  role: OrgRole;
  members: number;
  allowedDomains: string[];
  /** People from outside may be invited to its boards and projects. */
  guestsAllowed: boolean;
}

export interface Session {
  user: User | null;
  orgs: Org[];
  teams: Team[];
  oauth: { yandex: boolean; vk: boolean; /** Miro's OAuth app for imports. */ miro?: boolean };
  /** Published terms, privacy policy and consent (`{url}/terms` …); null — nothing is asked. */
  legal?: { url: string; version: string } | null;
  /** The signed-in person still has to accept the current edition. */
  consentRequired?: boolean;
}

export interface Board {
  id: string;
  title: string;
  teamId: string;
  teamName?: string;
  orgId: string | null;
  /** The space it lives in (crates/server/src/api/spaces.rs). */
  spaceId?: string;
  spaceName?: string;
  owner?: { id: string; name: string; color: string };
  createdAt?: number;
  updatedAt?: number;
  visitedAt?: number | null;
  deletedAt?: number | null;
  starred?: boolean;
  role: Role;
  thumbnail?: string | null;
  linkAccess: Access;
  teamAccess?: Access;
  orgAccess?: Access;
  anonymous?: boolean;
}

/** A team's container for boards and tracker projects (crates/server/src/api/spaces.rs). */
export type SpaceRole = 'admin' | 'editor' | 'commenter' | 'viewer';

export interface Space {
  id: string;
  teamId: string;
  teamName: string;
  orgId: string | null;
  name: string;
  description: string;
  icon: string | null;
  color: string | null;
  /** The team's default space: never deleted, things land there when nothing else is said. */
  isDefault: boolean;
  position: number;
  /** What the rest of the team gets; `none` — private, only those given a role. */
  teamAccess: Access;
  private: boolean;
  /** The person's own role there: admins change it, editors create in it. */
  role: SpaceRole | 'none';
  createdAt: number;
  boards: number;
  projects: number;
  /** Live pages of the documents. */
  pages: number;
}

/** Someone given a role in a space (or a team admin, who runs it anyway). */
export interface SpaceMember {
  userId: string;
  name: string;
  email: string;
  color: string;
  role: SpaceRole;
  teamRole: 'owner' | 'admin' | 'member' | null;
}

export interface SpacePeople {
  teamAccess: Access;
  teamName: string;
  teamSize: number;
  members: SpaceMember[];
  admins: SpaceMember[];
  canManage: boolean;
}

export interface Person {
  id: string;
  name: string;
  email: string;
  color: string;
}

export interface Sharing {
  myRole: Role;
  linkAccess: Access;
  teamAccess: Access;
  orgAccess: Access;
  team: { id: string; name: string };
  /** The board's space: `teamAccess` reaches its people, each up to their role there. */
  space?: { id: string; name: string; private: boolean };
  org: { id: string; name: string } | null;
  owner: Person;
  members: (Person & { role: MemberRole })[];
  invites: { id: string; email: string; role: MemberRole }[];
}

export interface Version {
  id: string;
  name: string | null;
  auto: boolean;
  createdAt: number;
  size: number;
  author: { id: string; name: string; color: string } | null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
  }
}

async function request<T>(method: string, path: string, body?: unknown, raw?: { type: string; data: Blob }): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin', headers: {} };
  if (raw) {
    (init.headers as Record<string, string>)['content-type'] = raw.type;
    init.body = raw.data;
  } else if (body !== undefined) {
    (init.headers as Record<string, string>)['content-type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  const res = await fetch(`/api${path}`, init);
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, json?.error ?? 'internal');
  return json as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
  upload: <T>(path: string, data: Blob, method = 'POST') => request<T>(method, path, undefined, { type: data.type, data }),
  /** Binary response (e.g. a version's document). */
  bytes: async (path: string): Promise<Uint8Array> => {
    const res = await fetch(`/api${path}`, { credentials: 'same-origin' });
    if (!res.ok) {
      const json = await res.json().catch(() => null);
      throw new ApiError(res.status, json?.error ?? 'internal');
    }
    return new Uint8Array(await res.arrayBuffer());
  },
};
